"use client";

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import { SUPABASE_URL, SUPABASE_ANON_KEY, CLOUDFLARE_URL } from '../lib/config';
import { useCommunication } from '../lib/CommunicationWrapper';

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

interface AuthContextType {
  credentialResponse: any;
  token: any;
  trialAccepted: boolean;
  loginSuccess: boolean;
  trialReady: boolean;
  loading: boolean;
  error: string | null;
  authStep: string;
  pendingGoogleCredential: any;
  ageVerified: boolean;
  afterAgeVerified: boolean;
  page: string;
  school: string | null;
  emailLogin: string | null;
  prices: any;
  selectedPrice: string;
  showConfetti: boolean;
  setAuthStep: (step: string) => void;
  setPendingGoogleCredential: (credential: any) => void;
  setAgeVerified: (verified: boolean) => void;
  setAfterAgeVerified: (verified: boolean) => void;
  setPage: (page: string) => void;
  setEmailLogin: (email: string) => void;
  setSelectedPrice: (price: string) => void;
  setPrices: (prices: any) => void;
  setAgeBand: (band: string) => void;
  setShowConfetti: (show: boolean) => void;
  setToken: (token: any) => void;
  setPreviouslyAppliedTheme: (theme: any) => void;
  postReapplyHandler: React.RefObject<any>;
}

const AuthStepContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuthStep = () => {
  const context = useContext(AuthStepContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthStepProvider = ({ children, initial }: { children: React.ReactNode, initial: { page: string, step: string } }) => {

  const { getOptions, updateOptions, closeModal, settings } = useCommunication();

  const [credentialResponse, setCredentialResponse] = useState({});
  const [token, setToken] = useState(null);
  const [trialAccepted, setTrialAccepted] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [trialReady, setTrialReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingGoogleCredential, setPendingGoogleCredential] = useState(null);
  const [ageVerified, setAgeVerified] = useState(false);
  const [afterAgeVerified, setAfterAgeVerified] = useState(false);
  const [page, setPage] = useState(initial["page"]);
  const [authStep, setAuthStep] = useState(initial["step"]);
  const [school, setSchool] = useState(null);
  const [emailLogin, setEmailLogin] = useState(null);
  const [prices, setPrices] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState("annually");
  const [ageBand, setAgeBand] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [completedSignup, setCompletedSignup] = useState(false);
  const [previouslyAppliedTheme, setPreviouslyAppliedTheme] = useState(null);

  const postReapplyHandler = useRef<Function>(null);

  const [loginState, setLoginState] = useState({
    success: false,
    error: null,
    loading: false,
  });

  const uuid = getOptions()?.analytics_id ?? crypto.randomUUID();

  const [redirectToSignup, setRedirectToSignup] = useState(false);

  const params = new URLSearchParams(window.location.search ?? "");
  const origin = params.get("origin");

  const resetAuth = () => {
    setCredentialResponse({});
    setToken({});
    setTrialAccepted(false);
    setLoginSuccess(false);
    setTrialReady(false);
    setLoading(false);
    setError(null);
  };

  const acceptTrial = () => {
    setTrialAccepted(true);
  };

  const fetchSchool = async () => {
    if (!origin) return;
    const { data, error } = await supabase.from("schools").select("*").eq("domain", origin);
    setSchool(data?.[0]?.name ?? null);
  }

  useEffect(() => {
    fetchSchool();
  }, []);

  const handleAuth = async (fn: any, type: "login" | "signup") => {

    setLoginState({
      success: false,
      error: null,
      loading: true,
      type: type,
    });
    try {
      const { data, error } = await fn;
      if (error) throw new Error(error);
      setToken(data?.session || data);
    } catch (err) {
      console.error("Login error:", err);
      setLoginState({
        success: false,
        error: "An unexpected error occurred during login. Please try again.",
        loading: false,
      });
    }
  }

  useEffect(() => {
    if (!token) return;
    handleAuthSuccess();
  }, [token]);

  const handleAuthFinish = async () => {


    updateOptions({
      "auth": token,
      "auth_state": "complete"
    });

    window?.top?.postMessage({
      type: "AUTH_LOGIN_SUCCESS",
      payload: {}
    }, "*");

    setTimeout(() => {
      closeModal();
    }, 200);

  }

  const handleAuthSuccess = async () => {
    setLoginState(prev => ({
      ...prev,
      success: true,
      error: null,
      loading: false,
    }));

    const response = await fetch(`${CLOUDFLARE_URL}/users/check-signup-completion`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${token?.access_token}`
      }
    });

    const { message, error } = await response.json();

    if (error) throw new Error(error);

    if (message.completed === false) {
      updateOptions({
        "should_show_spotlight": true,
      });
    }

    // check if the user has completed signup before when logging in
    // if they haven't completed, go through the signup flow


    // sign up -> [should show reapply] -> previouslyAppliedTheme -> claimTrial
    // sign up -> [!should show reapply] -> claimTrial

    // log in -> [should show reapply] -> previouslyAppliedTheme -> [!has completed] -> claimTrial -> handleAuthFinish
    // log in -> [!should show reapply] -> [!has completed] -> claimTrial -> handleAuthFinish
    // log in -> [should show reapply] -> previouslyAppliedTheme -> [has completed] -> handleAuthFinish
    // log in -> [!should show reapply] -> [has completed] -> handleAuthFinish

    const hasCompleted = message.completed === true;
    const shouldShowReapply = message.theme && getOptions()?.selected_theme?.code !== message.theme?.code;
    setCompletedSignup(message.completed);
    console.log("application", message.theme, getOptions(), getOptions().selected_theme?.code, shouldShowReapply)
    if (shouldShowReapply) setPreviouslyAppliedTheme(message.theme);


    if (loginState.type === "signup") {
      if (shouldShowReapply) {
        setAuthStep("previouslyAppliedTheme");
      } else {
        setAuthStep("claimTrial");
      }
      return;
    }

    if (loginState.type === "login") {
      if (!hasCompleted) {
        setAuthStep("ageVerification");
      } else if (hasCompleted && shouldShowReapply) {
        setAuthStep("previouslyAppliedTheme");
      } else if (hasCompleted && !shouldShowReapply) {
        handleAuthFinish();
      }
      return;
    }
  }

  const value = {
    // State
    credentialResponse,
    token,
    trialAccepted,
    loginSuccess,
    trialReady,
    loading,
    error,
    authStep,
    pendingGoogleCredential,
    ageVerified,
    afterAgeVerified,
    page,
    school,
    emailLogin,
    selectedPrice,
    prices,
    uuid,
    redirectToSignup,
    ageBand,
    showConfetti,
    completedSignup,
    loginState,
    previouslyAppliedTheme,
    postReapplyHandler,
    setRedirectToSignup,
    setCompletedSignup,
    setLoginState,
    handleAuthFinish,
    handleAuthSuccess,
    handleAuth,
    // Actions
    setCredentialResponse,
    setToken,
    setTrialAccepted,
    setLoginSuccess,
    setTrialReady,
    setLoading,
    setError,
    resetAuth,
    acceptTrial,
    setAuthStep,
    setPendingGoogleCredential,
    setAgeVerified,
    setAfterAgeVerified,
    setPage,
    setEmailLogin,
    setSelectedPrice,
    setPrices,
    setAgeBand,
    setShowConfetti,
    setPreviouslyAppliedTheme,
    // Supabase client for components that need it
    supabase
  };

  return <AuthStepContext.Provider value={value}>{children}</AuthStepContext.Provider>;
}; 